import React, { useState, useEffect } from 'react';
import Modali, { useModali } from './Modal';
import './ReceiptModal.scss';

/**
 * 車資收據 Modal Hook
 * @param {ReceiptModalOptions} options - 組件參數
 */
interface ReceiptModalOptions {
  /** 確認 callback，帶入 email */
  onConfirm: (email: string) => Promise<void> | void;
  /** 取消 callback */
  onCancel?: () => void;
  /** 預設 email 值 */
  defaultEmail?: string;
}

export const useReceiptModal = ({
  onConfirm,
  onCancel,
  defaultEmail = '',
}: ReceiptModalOptions) => {
  // email 狀態
  const [email, setEmail] = useState<string>(defaultEmail);
  const [hasError, setHasError] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [receiptModal, toggleReceiptModal] = useModali({
    animated: true,
    fullScreen: false,
    title: '',
    wrapperClass: 'receipt-modal__wrapper',
  });

  useEffect(() => {
    setEmail(defaultEmail);
  }, [defaultEmail]);

  /**
   * 處理 email 輸入變動
   * @param {React.ChangeEvent<HTMLInputElement>} event
   */
  const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setHasError(false);
  };

  /**
   * 按下確認按鈕
   */
  const onConfirmClick = async () => {
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setHasError(true);
      return;
    }
    setIsSubmitted(true);
    try {
      await onConfirm(email);
      toggleReceiptModal();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('確認收據時發生錯誤', error);
    } finally {
      setIsSubmitted(false);
    }
  };

  /**
   * 按下取消按鈕
   */
  const onCancelClick = () => {
    try {
      onCancel && onCancel();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('取消收據時發生錯誤', error);
    } finally {
      toggleReceiptModal();
    }
  };

  const ModalComponent: React.ReactNode = (
    <Modali.Modal {...receiptModal}>
      <div className="receipt-modal">
        <div className="receipt-modal__title-wrap">
          <p className="receipt-modal__title">車資收據</p>
        </div>
        <div className="receipt-modal__info">
          <p className="receipt-modal__info__title">請填寫收取電子收據的信箱</p>
        </div>
        <input
          type="email"
          placeholder="輸入您的 Email"
          value={email}
          onChange={onEmailChange}
          className={`receipt-modal__input${hasError ? ' receipt-modal__input--error' : ''}`}
        />
        {hasError && <div className="receipt-modal__error">請輸入有效的 Email</div>}
        <div className="receipt-modal__btn-row">
          <Modali.Button
            label="取消"
            color="#00ccc0"
            backgroundColor="transparent"
            isStyleDefault
            onClick={onCancelClick}
            disabled={isSubmitted}
          />
          <Modali.Button
            label="確認"
            color="#00ccc0"
            backgroundColor="transparent"
            isStyleDefault
            onClick={onConfirmClick}
            disabled={isSubmitted}
          />
        </div>
      </div>
    </Modali.Modal>
  );

  return {
    ReceiptModal: ModalComponent,
    receiptModal,
    toggleReceiptModal,
    setEmail,
  };
};
